/**
 * Current user's permission codes (cached), from get_current_user_permissions().
 * UI-level gating only — the database enforces the same permissions via RLS/RPCs.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/supabase.config';
import { useAuth } from '@/context/authContext';

export function usePermissions() {
  const { user } = useAuth();

  const { data: codes = [], isLoading } = useQuery({
    queryKey: ['current_user_permissions', user?.id],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('get_current_user_permissions');
      if (error) throw new Error(error.message);
      return ((data ?? []) as { code: string }[]).map((row) => row.code);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const set = useMemo(() => new Set(codes), [codes]);
  const can = useCallback((code: string) => set.has(code), [set]);
  const canAny = useCallback((...list: string[]) => list.some((c) => set.has(c)), [set]);

  return { can, canAny, isLoading };
}
